import type { DeviceRow } from "./devices.tsx";

export const FILTER_COLUMNS = [
  "name",
  "hostname",
  "presence",
  "status",
  "lastSeen",
  "os",
  "tags",
  "lastBoot",
  "user",
  "agent",
] as const;

export type FilterColumn = (typeof FILTER_COLUMNS)[number];

const FILTER_COLUMN_SET: ReadonlySet<string> = new Set(FILTER_COLUMNS);

export const URL_COLUMNS: ReadonlySet<FilterColumn> = new Set<FilterColumn>([
  "presence",
  "status",
  "os",
  "agent",
  "lastSeen",
  "lastBoot",
  "tags",
]);

export const RANGE_BUCKETS = ["lastHour", "last24h", "last7d", "last30d", "older", "never"] as const;

export type RangeBucket = (typeof RANGE_BUCKETS)[number];

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

const PRESENCE_VALUES = ["Online", "Offline"] as const;
const STATUS_VALUES = ["Pending", "Enrolled", "Revoked"] as const;

const CLOSED_VALUES: ReadonlyMap<FilterColumn, readonly string[]> = new Map<FilterColumn, readonly string[]>([
  ["presence", PRESENCE_VALUES],
  ["status", STATUS_VALUES],
  ["lastSeen", RANGE_BUCKETS],
  ["lastBoot", RANGE_BUCKETS],
]);

const RANGE_LABELS: ReadonlyMap<string, string> = new Map<RangeBucket, string>([
  ["lastHour", "Last hour"],
  ["last24h", "Last 24 hours"],
  ["last7d", "Last 7 days"],
  ["last30d", "Last 30 days"],
  ["older", "Older"],
]);

export const UNTAGGED_LABEL = "(untagged)";
export const PARTIAL_BANNER = "Showing the newest 5,000 devices; older devices are not loaded";

export type Filters = {
  readonly values: ReadonlyMap<FilterColumn, ReadonlySet<string>>;
  readonly untagged: boolean;
};

export const NO_FILTERS: Filters = { values: new Map(), untagged: false };

export type SortDirection = "asc" | "desc";

export type Sort = { readonly column: FilterColumn; readonly direction: SortDirection };

export const DEFAULT_SORT: Sort = { column: "name", direction: "asc" };

const SORT_BY_TOKEN: ReadonlyMap<string, Sort> = new Map(
  FILTER_COLUMNS.flatMap((column) =>
    (["asc", "desc"] as const).map((direction): [string, Sort] => [`${column}.${direction}`, { column, direction }]),
  ),
);

export function sortToken(sort: Sort): string {
  return `${sort.column}.${sort.direction}`;
}

export function tagLabel(tag: { key: string; value: string }): string {
  return tag.value.length > 0 ? `${tag.key}:${tag.value}` : tag.key;
}

export function osLines(label: string): { primary: string; secondary: string } {
  const at = label.indexOf(" (OS Build");
  if (at > 0) return { primary: label.slice(0, at), secondary: label.slice(at + 1) };
  return { primary: label, secondary: "" };
}

function instantMs(instant: string | null): number | null {
  if (instant === null) return null;
  const at = Date.parse(instant);
  return Number.isNaN(at) ? null : at;
}

export function rangeBucket(instant: string | null, nowMs: number): RangeBucket {
  const at = instantMs(instant);
  if (at === null) return "never";
  const age = nowMs - at;
  if (age < HOUR_MS) return "lastHour";
  if (age < DAY_MS) return "last24h";
  if (age < 7 * DAY_MS) return "last7d";
  if (age < 30 * DAY_MS) return "last30d";
  return "older";
}

export function optionLabel(column: FilterColumn, value: string): string {
  if (column !== "lastSeen" && column !== "lastBoot") return value;
  if (value === "never") return column === "lastSeen" ? "No heartbeat" : "Unknown";
  return RANGE_LABELS.get(value) ?? value;
}

function facetValue(row: DeviceRow, column: Exclude<FilterColumn, "tags">, nowMs: number): string {
  switch (column) {
    case "name":
      return row.name;
    case "hostname":
      return row.hostname;
    case "presence":
      return row.presence;
    case "status":
      return row.status;
    case "lastSeen":
      return rangeBucket(row.lastHeartbeatAt, nowMs);
    case "os":
      return osLines(row.osLabel).primary;
    case "lastBoot":
      return rangeBucket(row.lastBootAt, nowMs);
    case "user":
      return row.loggedOnUser;
    case "agent":
      return row.agentVersion;
  }
}

type RowFacets = {
  readonly single: ReadonlyMap<FilterColumn, string>;
  readonly tags: ReadonlySet<string>;
};

function rowFacets(row: DeviceRow, nowMs: number): RowFacets {
  const single = new Map<FilterColumn, string>();
  for (const column of FILTER_COLUMNS) {
    if (column !== "tags") single.set(column, facetValue(row, column, nowMs));
  }
  return { single, tags: new Set(row.tags.map(tagLabel)) };
}

function tagsActive(filters: Filters): boolean {
  return (filters.values.get("tags")?.size ?? 0) > 0 || filters.untagged;
}

function passes(facets: RowFacets, column: FilterColumn, filters: Filters): boolean {
  if (column === "tags") {
    if (!tagsActive(filters)) return true;
    if (filters.untagged && facets.tags.size === 0) return true;
    const selected = filters.values.get("tags");
    if (!selected) return false;
    for (const label of facets.tags) if (selected.has(label)) return true;
    return false;
  }
  const selected = filters.values.get(column);
  if (!selected || selected.size === 0) return true;
  return selected.has(facets.single.get(column) ?? "");
}

export function filterRows(rows: readonly DeviceRow[], filters: Filters, nowMs: number): DeviceRow[] {
  return rows.filter((row) => {
    const facets = rowFacets(row, nowMs);
    return FILTER_COLUMNS.every((column) => passes(facets, column, filters));
  });
}

export type FilterOption = { readonly value: string; readonly label: string; readonly count: number };

export type ColumnFacet = {
  readonly options: readonly FilterOption[];
  readonly untagged: number | null;
};

export type Facets = {
  readonly rows: readonly DeviceRow[];
  readonly columns: ReadonlyMap<FilterColumn, ColumnFacet>;
};

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function orderedValues(column: FilterColumn, seen: Iterable<string>): string[] {
  const present = new Set(seen);
  const closed = CLOSED_VALUES.get(column);
  if (closed) return closed.filter((value) => present.has(value));
  return [...present].sort((left, right) => collator.compare(left, right));
}

export function computeFacets(rows: readonly DeviceRow[], filters: Filters, nowMs: number): Facets {
  const counts = new Map<FilterColumn, Map<string, number>>(FILTER_COLUMNS.map((column) => [column, new Map()]));
  let untaggedSeen = false;
  let untaggedCount = 0;
  const passing: DeviceRow[] = [];
  for (const row of rows) {
    const facets = rowFacets(row, nowMs);
    const failed = FILTER_COLUMNS.filter((column) => !passes(facets, column, filters));
    if (failed.length === 0) passing.push(row);
    for (const column of FILTER_COLUMNS) {
      const counted = failed.length === 0 || (failed.length === 1 && failed[0] === column);
      const tally = counts.get(column);
      if (!tally) continue;
      const values = column === "tags" ? facets.tags : [facets.single.get(column) ?? ""];
      for (const value of values) tally.set(value, (tally.get(value) ?? 0) + (counted ? 1 : 0));
      if (column === "tags" && facets.tags.size === 0) {
        untaggedSeen = true;
        if (counted) untaggedCount += 1;
      }
    }
  }
  const columns = new Map<FilterColumn, ColumnFacet>();
  for (const column of FILTER_COLUMNS) {
    const tally = counts.get(column) ?? new Map<string, number>();
    columns.set(column, {
      options: orderedValues(column, tally.keys()).map((value) => ({
        value,
        label: optionLabel(column, value),
        count: tally.get(value) ?? 0,
      })),
      untagged: column === "tags" && untaggedSeen ? untaggedCount : null,
    });
  }
  return { rows: passing, columns };
}

export function filterSignature(filters: Filters): string {
  const parts: string[] = [filters.untagged ? "untagged" : ""];
  for (const column of FILTER_COLUMNS) {
    const selected = filters.values.get(column);
    if (!selected || selected.size === 0) continue;
    parts.push(column, ...[...selected].sort());
  }
  return parts.join("\u0000");
}

export function createFacetMemo(compute: typeof computeFacets = computeFacets) {
  let last: { rows: readonly DeviceRow[]; signature: string; nowMs: number; facets: Facets } | null = null;
  return (rows: readonly DeviceRow[], filters: Filters, nowMs: number): Facets => {
    const signature = filterSignature(filters);
    if (last && last.rows === rows && last.signature === signature && last.nowMs === nowMs) return last.facets;
    const facets = compute(rows, filters, nowMs);
    last = { rows, signature, nowMs, facets };
    return facets;
  };
}

export function toggleValue(filters: Filters, column: FilterColumn, value: string): Filters {
  const values = new Map(filters.values);
  const selected = new Set(values.get(column) ?? []);
  if (selected.has(value)) selected.delete(value);
  else selected.add(value);
  if (selected.size === 0) values.delete(column);
  else values.set(column, selected);
  return { values, untagged: filters.untagged };
}

export function toggleUntagged(filters: Filters): Filters {
  return { values: filters.values, untagged: !filters.untagged };
}

export function selectedCount(filters: Filters, column: FilterColumn): number {
  const size = filters.values.get(column)?.size ?? 0;
  return column === "tags" && filters.untagged ? size + 1 : size;
}

export function hasFilters(filters: Filters): boolean {
  return filters.untagged || [...filters.values.values()].some((selected) => selected.size > 0);
}

export function pruneFilters(
  filters: Filters,
  rows: readonly DeviceRow[],
  nowMs: number,
  load: { partial: boolean },
): Filters {
  if (load.partial) return filters;
  const universe = computeFacets(rows, NO_FILTERS, nowMs).columns;
  let changed = false;
  const values = new Map<FilterColumn, ReadonlySet<string>>();
  for (const [column, selected] of filters.values) {
    const known = new Set(universe.get(column)?.options.map((option) => option.value) ?? []);
    const kept = new Set([...selected].filter((value) => known.has(value)));
    if (kept.size !== selected.size) changed = true;
    if (kept.size > 0) values.set(column, kept);
  }
  const untagged = filters.untagged && (universe.get("tags")?.untagged ?? null) !== null;
  if (untagged !== filters.untagged) changed = true;
  return changed ? { values, untagged } : filters;
}

export type SortKey = string | number;

function sortKey(row: DeviceRow, column: FilterColumn): SortKey {
  switch (column) {
    case "name":
      return row.name;
    case "hostname":
      return row.hostname;
    case "presence":
      return row.presence;
    case "status":
      return row.status;
    case "lastSeen":
      return instantMs(row.lastHeartbeatAt) ?? Number.NEGATIVE_INFINITY;
    case "os":
      return row.osLabel;
    case "tags":
      return row.tags.map(tagLabel).join(", ");
    case "lastBoot":
      return instantMs(row.lastBootAt) ?? Number.NEGATIVE_INFINITY;
    case "user":
      return row.loggedOnUser;
    case "agent":
      return row.agentVersion;
  }
}

function ordinal(left: SortKey, right: SortKey): number {
  if (left < right) return -1;
  return left > right ? 1 : 0;
}

function compareKeys(left: SortKey, right: SortKey): number {
  if (typeof left === "number" && typeof right === "number") return ordinal(left, right);
  return collator.compare(String(left), String(right));
}

export function sortRows(rows: readonly DeviceRow[], sort: Sort): DeviceRow[] {
  const sign = sort.direction === "asc" ? 1 : -1;
  return rows
    .map((row) => ({ row, key: sortKey(row, sort.column) }))
    .sort((left, right) => sign * compareKeys(left.key, right.key) || ordinal(left.row.deviceId, right.row.deviceId))
    .map((entry) => entry.row);
}

export const MAX_SEARCH_CHARS = 2000;
export const MAX_URL_COLUMNS = 10;
export const MAX_URL_VALUES = 50;
export const MAX_VALUE_CHARS = 128;
const FILTER_PREFIX = "f.";
const UNTAGGED_PARAM = "f.untagged";
const SORT_PARAM = "sort";

export type TableQuery = {
  readonly filters: Filters;
  readonly sort: Sort;
  readonly frozen: boolean;
};

function urlColumn(key: string): FilterColumn | null {
  if (!key.startsWith(FILTER_PREFIX)) return null;
  const name = key.slice(FILTER_PREFIX.length);
  if (!FILTER_COLUMN_SET.has(name)) return null;
  const column = name as FilterColumn;
  return URL_COLUMNS.has(column) ? column : null;
}

export function readTableQuery(search: string): TableQuery {
  if (search.length > MAX_SEARCH_CHARS) return { filters: NO_FILTERS, sort: DEFAULT_SORT, frozen: true };
  const params = new URLSearchParams(search);
  const values = new Map<FilterColumn, Set<string>>();
  let total = 0;
  for (const [key, value] of params) {
    const column = urlColumn(key);
    if (column === null || value.length > MAX_VALUE_CHARS || total >= MAX_URL_VALUES) continue;
    const closed = CLOSED_VALUES.get(column);
    if (closed && !closed.includes(value)) continue;
    let selected = values.get(column);
    if (!selected) {
      if (values.size >= MAX_URL_COLUMNS) continue;
      selected = new Set();
      values.set(column, selected);
    }
    if (selected.has(value)) continue;
    selected.add(value);
    total += 1;
  }
  return {
    filters: { values, untagged: params.get(UNTAGGED_PARAM) === "1" },
    sort: SORT_BY_TOKEN.get(params.get(SORT_PARAM) ?? "") ?? DEFAULT_SORT,
    frozen: false,
  };
}

function ownedSegment(segment: string): boolean {
  const [key = ""] = new URLSearchParams(segment).keys();
  return key === SORT_PARAM || key.startsWith(FILTER_PREFIX);
}

export function writeTableQuery(search: string, filters: Filters, sort: Sort): string {
  const body = search.startsWith("?") ? search.slice(1) : search;
  const kept = body.split("&").filter((segment) => segment.length > 0 && !ownedSegment(segment));
  const params = new URLSearchParams();
  for (const column of FILTER_COLUMNS) {
    if (!URL_COLUMNS.has(column)) continue;
    for (const value of filters.values.get(column) ?? []) params.append(`${FILTER_PREFIX}${column}`, value);
  }
  if (filters.untagged) params.append(UNTAGGED_PARAM, "1");
  const token = sortToken(sort);
  if (token !== sortToken(DEFAULT_SORT)) params.append(SORT_PARAM, token);
  const owned = params.toString();
  const query = [...kept, ...(owned.length > 0 ? [owned] : [])].join("&");
  return query.length === 0 ? "" : `?${query}`;
}

export type TableId =
  | "devices"
  | "recordings"
  | "audit"
  | "settings-devices"
  | "settings-users"
  | "settings-roles"
  | "settings-policies"
  | "settings-reason";

export const DEVICES_TABLE_ID: TableId = "devices";
export const RECORDINGS_TABLE_ID: TableId = "recordings";
export const AUDIT_TABLE_ID: TableId = "audit";
export const SETTINGS_DEVICES_TABLE_ID: TableId = "settings-devices";
export const SETTINGS_USERS_TABLE_ID: TableId = "settings-users";
export const SETTINGS_ROLES_TABLE_ID: TableId = "settings-roles";
export const SETTINGS_POLICIES_TABLE_ID: TableId = "settings-policies";
export const SETTINGS_REASON_TABLE_ID: TableId = "settings-reason";
export const MAX_COLUMN_WIDTH = 640;
export const MAX_WIDTHS_CHARS = 2048;

export type WidthSpec = { readonly key: string; readonly minWidth: number; readonly defaultWidth: number };

export function widthsStorageKey(tableId: TableId): string {
  return `wylo-rc.table.${tableId}.widths`;
}

export function clampWidth(spec: WidthSpec, width: number): number {
  return Math.min(MAX_COLUMN_WIDTH, Math.max(spec.minWidth, Math.round(width)));
}

export function defaultWidths(columns: readonly WidthSpec[]): Map<string, number> {
  return new Map(columns.map((column) => [column.key, column.defaultWidth]));
}

export function parseWidths(raw: string | null, columns: readonly WidthSpec[]): Map<string, number> {
  const widths = defaultWidths(columns);
  if (raw === null || raw.length > MAX_WIDTHS_CHARS) return widths;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return widths;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return widths;
  for (const column of columns) {
    if (!Object.hasOwn(parsed, column.key)) continue;
    const value: unknown = Object.getOwnPropertyDescriptor(parsed, column.key)?.value;
    if (typeof value === "number" && Number.isFinite(value)) widths.set(column.key, clampWidth(column, value));
  }
  return widths;
}

export function serializeWidths(widths: ReadonlyMap<string, number>): string {
  return JSON.stringify(Object.fromEntries(widths));
}
